import { z } from 'zod';
import { FailureDiagnosticEvidenceSchema } from './issue-evidence.js';
import { IssueSeveritySchema } from './issue-core.js';
import {
  DIAGNOSTIC_LIMIT,
  TITLE_LIMIT,
  URL_LIMIT,
  UUID,
  optionalCategoryKey,
  optionalLimitedString,
  optionalSafeUrl,
  requiredLimitedString,
} from './issues-schema-utils.js';

const ManualEvidenceInput = z
  .array(FailureDiagnosticEvidenceSchema)
  .max(50, 'At most 50 evidence items may be attached')
  .optional()
  .nullable()
  .transform((value) => value ?? null);

const OptionalUuid = UUID.optional()
  .nullable()
  .transform((value) => value ?? null);

/**
 * Ties an issue to the product behaviour it breaks: a capability (`CAP-…`
 * code, resolved within the issue's application, or `pcap_` id), optionally
 * narrowed to one of its rules (`prule_` id).
 */
export const IssueProductMapLinkSchema = z.object({
  capability: z.string().trim().min(1, 'capability is required'),
  rule: z.string().trim().min(1, 'rule must not be empty').optional(),
});
export type IssueProductMapLink = z.infer<typeof IssueProductMapLinkSchema>;

/**
 * The product-map link a Cofactor agent names on its own filing tool: the
 * capability (and optionally the rule) the bug breaks, chosen by the agent
 * from the map it was shown, never inferred from a URL. Both fields are
 * optional because the agent leaves them out whenever it is not sure.
 *
 * Spread into AI tool input schemas, so no `.min()` or `.transform()`: a
 * schema rejection would cost the whole filing, while a bad value here only
 * costs the link (`apps/api/src/issues/agent-product-map-link.ts`).
 */
export const AgentProductMapLinkFields = {
  capability: z
    .string()
    .optional()
    .describe(
      'The product-map capability code (CAP-…) this bug breaks. Set it only when the bug clearly breaks that capability; leave it out otherwise.'
    ),
  rule: z
    .string()
    .optional()
    .describe(
      "The id of the rule this bug breaks, one of that capability's rules. Set it only together with capability, and only when the bug clearly breaks that rule."
    ),
};
export const AgentProductMapLinkSchema = z.object(AgentProductMapLinkFields);
export type AgentProductMapLink = z.infer<typeof AgentProductMapLinkSchema>;

const ManualOccurrenceFields = {
  body: optionalLimitedString(DIAGNOSTIC_LIMIT),
  primaryUrl: optionalSafeUrl(URL_LIMIT),
  runId: OptionalUuid,
  /**
   * Link the issue to this failing assertion of `runId` (an assertion result
   * `key`). Requires `runId`; validated against the run's failing verdicts.
   * Once linked, the assertion's next failure is issue-covered.
   */
  subjectAssertionKey: OptionalUuid,
  evidence: ManualEvidenceInput,
  /** Link the issue to a capability (and rule) in the same call; a refused link files nothing. */
  productMapLink: IssueProductMapLinkSchema.optional(),
};

export const CreateManualIssuePayloadSchema = z.object({
  propertyId: UUID,
  issueTitle: requiredLimitedString('Issue title', TITLE_LIMIT),
  issueSeverity: IssueSeveritySchema,
  issueCategoryId: OptionalUuid,
  issueCategoryKey: optionalCategoryKey,
  ...ManualOccurrenceFields,
});
export type CreateManualIssuePayload = z.infer<typeof CreateManualIssuePayloadSchema>;

export const AddIssueOccurrencePayloadSchema = z.object({
  ...ManualOccurrenceFields,
});
export type AddIssueOccurrencePayload = z.infer<typeof AddIssueOccurrencePayloadSchema>;

/**
 * Edit an occurrence's report. Each field is optional because a caller edits
 * only what it names (`cofactor issues update --summary` sends just `summary`);
 * at least one is required. `evidence` replaces the occurrence's whole list —
 * the CLI reads the current list, applies its add/replace/remove, and sends
 * the result.
 */
export const UpdateIssueOccurrencePayloadSchema = z
  .object({
    summary: requiredLimitedString('Summary', DIAGNOSTIC_LIMIT).optional(),
    reproSteps: requiredLimitedString('Repro steps', DIAGNOSTIC_LIMIT).optional(),
    evidence: z
      .array(FailureDiagnosticEvidenceSchema)
      .max(50, 'At most 50 evidence items may be attached')
      .optional(),
  })
  .refine(
    (value) =>
      value.summary !== undefined || value.reproSteps !== undefined || value.evidence !== undefined,
    'Nothing to update: pass summary, reproSteps, or evidence'
  );
export type UpdateIssueOccurrencePayload = z.infer<typeof UpdateIssueOccurrencePayloadSchema>;

/** Close the path issue as a duplicate of `canonicalIssueId` (a uuid or `iss_` id). */
export const MarkIssueDuplicatePayloadSchema = z.object({
  canonicalIssueId: z.string().trim().min(1, 'canonicalIssueId is required'),
});
export type MarkIssueDuplicatePayload = z.infer<typeof MarkIssueDuplicatePayloadSchema>;
